export const disease2Module = {
  id: "disease2",
  title: "Kidney Diseases: Tubulointerstitial & Vascular",
  sections: [
    {
      type: "text",
      title: "Acute Tubular Necrosis (ATN)",
      content: `
        <p><span class="glossary-term">Acute tubular necrosis (ATN)</span> is the most common cause of intrinsic (parenchymal) AKI, accounting for ~75% of cases. The <strong>S3 segment of the PCT</strong> and <strong>thick ascending limb</strong> are most vulnerable due to their high metabolic demand and relatively limited blood supply.</p>

        <div class="highlight-box">
          <p><strong>Causes</strong></p>
          <ul>
            <li><strong>Ischaemic ATN</strong> — prolonged hypoperfusion: sepsis (most common), hypovolaemia, major surgery, cardiac arrest, burns, hepatorenal syndrome</li>
            <li><strong>Nephrotoxic ATN</strong>:
              <ul>
                <li>Aminoglycosides (accumulate in PCT lysosomes; risk reduced by once-daily dosing and trough monitoring)</li>
                <li>Cisplatin, carboplatin (direct tubular toxicity + vasoconstriction)</li>
                <li>Contrast dye (vasoconstriction + direct toxicity; prevent with IV saline hydration)</li>
                <li>Myoglobin (rhabdomyolysis — elevated CK, dark urine, ↑K⁺, ↑phosphate, ↑urate)</li>
                <li>Haemoglobin (intravascular haemolysis)</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Three clinical phases</strong></p>
          <ul>
            <li><strong>Initiation</strong> — the injury occurs; oliguric onset</li>
            <li><strong>Maintenance</strong> (~1–3 weeks) — oliguric; hyperkalaemia, hyperphosphataemia, metabolic acidosis, fluid overload; requires dialysis if severe</li>
            <li><strong>Recovery/polyuric phase</strong> — tubular cells regenerate; GFR improves; large urine output (tubules can't concentrate yet); risk of hypokalaemia and hypomagnesaemia — replace electrolytes</li>
          </ul>
        </div>

        <p><strong>Urine findings</strong>: <span class="purple-term">Muddy brown granular casts</span> (sloughed tubular epithelial cells), FENa &gt;2%, urine Na⁺ &gt;40 mmol/L, urine osmolality ~300 mOsm (isosthenuria).</p>

        <div class="mnemonic-card">
          <div class="mnemonic-title">ATN Recovery Phase Hazard</div>
          <div class="mnemonic-content">During recovery, tubular cells regenerate but cannot yet concentrate or reabsorb normally → <strong>large urine output with electrolyte losses</strong>. Monitor and replace K⁺ and Mg²⁺ — patients can swing from hyperkalaemia (maintenance) to hypokalaemia (recovery).</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient post-cardiac surgery develops oliguria. Urine shows muddy brown granular casts, FENa 3.8%, and urine osmolality 295 mOsm/kg. What is the diagnosis?",
      options: [
        "Prerenal AKI — give IV fluids",
        "Acute tubular necrosis — intrinsic AKI from ischaemia",
        "Acute interstitial nephritis — from perioperative antibiotics",
        "Postrenal AKI — urinary obstruction",
      ],
      correct: 1,
      explanation:
        "Muddy brown granular casts (sloughed tubular cells), FENa >2%, and isosthenuria (~300 mOsm) confirm intrinsic AKI from ATN. The post-cardiac surgery setting indicates ischaemic ATN from prolonged hypoperfusion. In prerenal AKI, FENa <1% and urine osmolality >500 mOsm (intact tubular concentrating ability). IV fluids alone are insufficient; the damaged tubules need time to regenerate.",
    },
    {
      type: "question",
      text: "A patient with rhabdomyolysis after a crush injury develops AKI. Urine is dark brown. Which toxin is responsible for tubular injury and what metabolic complications are expected?",
      options: [
        "Creatinine — elevated CK causes glomerular injury",
        "Myoglobin — directly toxic to tubular cells; hyperkalaemia, hyperphosphataemia, hyperuricaemia, and metabolic acidosis expected",
        "Haemoglobin — intravascular haemolysis deposits in tubules",
        "Uric acid — causes obstructive nephropathy",
      ],
      correct: 1,
      explanation:
        "Rhabdomyolysis releases myoglobin from damaged muscle. Myoglobin is directly toxic to PCT cells and can precipitate in tubular lumens. The dark brown urine reflects myoglobinuria (urine dipstick positive for haem but no RBCs on microscopy). Metabolic consequences include hyperkalaemia (K⁺ from damaged cells), hyperphosphataemia, hyperuricaemia, and hypocalcaemia (calcium sequestered in damaged muscle). Treatment: aggressive IV saline hydration.",
    },
    {
      type: "text",
      title: "Acute Interstitial Nephritis (AIN)",
      content: `
        <p><span class="glossary-term">Acute interstitial nephritis (AIN)</span> is an inflammatory infiltration of the renal interstitium (lymphocytes, eosinophils), causing AKI. <strong>Drug-induced AIN accounts for ~70–75%</strong> of cases.</p>

        <div class="highlight-box">
          <p><strong>Drug causes (most important to know)</strong></p>
          <ul>
            <li><strong>PPIs</strong> (e.g., omeprazole, lansoprazole) — now one of the most common causes; insidious onset; often no rash or eosinophilia</li>
            <li><strong>NSAIDs</strong> — delayed, insidious; can also cause MCD-type nephrotic syndrome</li>
            <li><strong>Antibiotics</strong> — penicillins (methicillin = classic), cephalosporins, rifampicin, sulphonamides, quinolones</li>
            <li><strong>Allopurinol</strong> — granulomatous AIN</li>
            <li><strong>Diuretics</strong>, checkpoint inhibitors (anti-PD-1)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Classic triad</strong> (only 10–30% have all three)</p>
          <ul>
            <li><strong>Fever</strong></li>
            <li><strong>Rash</strong> (maculopapular)</li>
            <li><strong>Eosinophilia</strong></li>
          </ul>
          <p>NSAIDs and PPIs often cause AIN <em>without</em> the classic triad — making diagnosis difficult.</p>
        </div>

        <div class="highlight-box">
          <p><strong>Urine findings</strong></p>
          <ul>
            <li><span class="purple-term">WBC casts</span> (white cell casts in tubular lumens)</li>
            <li>Eosinophiluria on Hansel stain (not specific)</li>
            <li>Sterile pyuria (white cells without bacteria)</li>
            <li>Mild haematuria, mild proteinuria</li>
          </ul>
        </div>

        <p><strong>Non-drug causes</strong>: Infections (Legionella, CMV, EBV, Hantavirus, Leptospira); systemic disease (sarcoidosis → granulomatous AIN; SLE; Sjögren's).</p>

        <p><strong>Treatment</strong>: Stop the offending drug. Steroids (methylprednisolone → prednisolone) if GFR continues to decline after 5–7 days without improvement. Early treatment improves renal recovery — delay leads to irreversible interstitial fibrosis.</p>
      `,
    },
    {
      type: "question",
      text: "A 65-year-old patient on long-term omeprazole develops gradually worsening renal function over 6 months with no haematuria, minimal proteinuria, and sterile pyuria. There is no rash or eosinophilia. What is the most likely diagnosis?",
      options: [
        "Prerenal AKI from poor fluid intake",
        "Drug-induced acute interstitial nephritis from the PPI",
        "Membranous nephropathy",
        "Urinary tract infection with partially treated pyelonephritis",
      ],
      correct: 1,
      explanation:
        "PPIs are now one of the most common causes of drug-induced AIN, particularly because the classic triad (fever, rash, eosinophilia) is frequently absent — making the diagnosis easy to miss. Insidious onset over months, sterile pyuria, and mild proteinuria without haematuria in a patient on long-term PPIs should prompt consideration of PPI-induced AIN. Management: stop the PPI, switch to H2 blocker, and consider steroids if GFR does not recover.",
    },
    {
      type: "question",
      text: "A urine microscopy report mentions 'WBC casts'. What is the significance of this finding?",
      options: [
        "Glomerular haematuria — diagnostic of GN",
        "Upper urinary tract inflammation — either pyelonephritis or interstitial nephritis",
        "Tubular cell sloughing — diagnostic of ATN",
        "Urinary tract infection confined to the bladder",
      ],
      correct: 1,
      explanation:
        "WBC casts (white cell casts) form when white blood cells accumulate and are trapped within tubular casts. They indicate upper urinary tract disease — either acute pyelonephritis (pyonephrosis, upper UTI) or tubulointerstitial nephritis (AIN). RBC casts = glomerulonephritis; muddy brown granular casts = ATN; fatty casts = nephrotic syndrome; hyaline casts = normal or pre-renal.",
    },
    {
      type: "text",
      title: "Pyelonephritis & Reflux Nephropathy",
      content: `
        <p><span class="glossary-term">Acute pyelonephritis</span> is infection of the renal parenchyma and pelvis, usually ascending from the bladder. It is a common cause of sepsis, particularly in women and immunocompromised patients.</p>

        <div class="highlight-box">
          <p><strong>Acute pyelonephritis</strong></p>
          <ul>
            <li><strong>Organisms</strong>: E. coli (80%), Klebsiella, Proteus (↑pH, struvite stones), Enterococcus</li>
            <li><strong>Features</strong>: fever + rigors, loin pain, CVAT (costovertebral angle tenderness), LUTS (dysuria, frequency)</li>
            <li><strong>Urine</strong>: WBC casts, leucocyturia, nitrites, bacteriuria</li>
            <li><strong>Treatment</strong>: co-amoxiclav or cephalosporin (oral, 7–14 days); IV piperacillin-tazobactam or gentamicin if septic</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Complications of pyelonephritis</strong></p>
          <ul>
            <li><strong>Perinephric abscess</strong> — failure to improve after 72 hours of antibiotics; CT-guided drainage required</li>
            <li><strong>Emphysematous pyelonephritis</strong> — gas in renal parenchyma on CT; diabetic patients, E. coli or Klebsiella; surgical emergency (nephrectomy may be required)</li>
            <li><strong>Pyonephrosis</strong> — obstructed infected kidney (stone or stricture); emergency drainage (ureteric stent or nephrostomy) + antibiotics</li>
            <li><strong>Xanthogranulomatous pyelonephritis (XGP)</strong> — foamy macrophages replacing renal parenchyma; associated with staghorn calculi + Proteus; CT "bear paw" sign; mimics RCC → often nephrectomy</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Reflux nephropathy (chronic pyelonephritis)</strong></p>
          <ul>
            <li><span class="glossary-term">Vesicoureteric reflux (VUR)</span> → recurrent pyelonephritis → cortical scars overlying blunted calyces</li>
            <li>Most common cause of CKD in children</li>
            <li>Investigate with DMSA scan (cortical scars), MCUG (VUR grading I–V)</li>
            <li>Consequences: hypertension (renovascular mechanism), proteinuria, CKD, ESKD</li>
            <li>Treatment: long-term low-dose prophylactic antibiotics; ureteric reimplantation for high-grade VUR</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A diabetic patient with pyelonephritis does not improve after 4 days of IV antibiotics. CT shows gas within the renal parenchyma. What is the diagnosis and how is it managed?",
      options: [
        "Perinephric abscess — CT-guided drainage",
        "Emphysematous pyelonephritis — surgical emergency, often requiring nephrectomy",
        "Pyonephrosis — ureteric stenting to relieve obstruction",
        "Xanthogranulomatous pyelonephritis — nephrectomy electively",
      ],
      correct: 1,
      explanation:
        "Emphysematous pyelonephritis is a life-threatening necrotising infection of the kidney producing gas in the renal parenchyma. It occurs almost exclusively in diabetic patients, caused by gas-forming organisms (E. coli, Klebsiella). CT is diagnostic. It carries >40% mortality without surgery. Management: emergency percutaneous drainage + IV antibiotics; nephrectomy if deteriorating. This differs from perinephric abscess (gas outside the kidney) or pyonephrosis (infected obstructed system).",
    },
    {
      type: "question",
      text: "A 5-year-old girl has her third episode of pyelonephritis. DMSA scan shows cortical scarring of the upper poles bilaterally. What underlying condition is most likely and what investigation confirms it?",
      options: [
        "Bilateral renal artery stenosis — confirmed by Doppler ultrasound",
        "Vesicoureteric reflux (VUR) — confirmed by micturating cystourethrogram (MCUG)",
        "ADPKD — confirmed by ultrasound showing cysts",
        "Bartter syndrome — confirmed by electrolyte analysis",
      ],
      correct: 1,
      explanation:
        "Recurrent pyelonephritis in a child with cortical scars overlying the upper poles is the classic pattern of reflux nephropathy (chronic pyelonephritis from VUR). DMSA scintigraphy shows cortical defects (scars); MCUG (micturating cystourethrogram) directly demonstrates the reflux and grades it (I–V). VUR is the most common cause of CKD in children. Management includes antibiotic prophylaxis and, for high-grade VUR, surgical ureteric reimplantation.",
    },
    {
      type: "text",
      title: "Renal Artery Stenosis & Hypertensive Nephropathy",
      content: `
        <p>Vascular disease affecting the renal arteries can cause hypertension and ischaemic nephropathy. The two main causes have distinct clinical profiles.</p>

        <div class="highlight-box">
          <p><strong>Atherosclerotic renal artery stenosis</strong></p>
          <ul>
            <li>Older patients, significant cardiovascular risk factors</li>
            <li>Affects the <strong>proximal/ostial 1/3</strong> of the renal artery</li>
            <li>Clinical clues: resistant hypertension, <span class="purple-term">flash pulmonary oedema</span> (bilateral RAS), abdominal bruit, ↑creatinine with ACEi/ARBs, unexplained CKD</li>
            <li>Investigations: renal artery Doppler (screening), CT/MR angiography (definitive)</li>
            <li>Treatment: statin + antiplatelet (medical therapy); renal artery stenting has limited evidence for BP but may preserve renal function in selected patients</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Fibromuscular dysplasia (FMD)</strong></p>
          <ul>
            <li>Young women (typically 20–40 years)</li>
            <li>Fibrous overgrowth of media/adventitia — not atheromatous</li>
            <li>Affects the <strong>distal 2/3</strong> of the renal artery (and carotid arteries)</li>
            <li>Angiography: <span class="purple-term">"string of beads"</span> — alternating stenoses and aneurysms</li>
            <li>Treatment: balloon angioplasty (without stent) — highly effective, often curative</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Malignant hypertension</strong></p>
          <ul>
            <li>BP &gt;180/120 mmHg with end-organ damage (hypertensive emergency)</li>
            <li>Renal: fibrinoid necrosis of arterioles, "onion-skin" (hyperplastic arteriolosclerosis), AKI, proteinuria, MAHA</li>
            <li>Other features: papilloedema, flame haemorrhages, confusion, chest pain</li>
            <li>Treatment: IV labetalol or nicardipine; lower BP <em>gradually</em> over hours (too rapid → cerebral/renal ischaemia); target 25% reduction in MAP over first hour</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A young woman with resistant hypertension has a renal angiogram showing alternating stenoses and dilations ('string of beads') in the distal renal artery. What is the diagnosis and the preferred treatment?",
      options: [
        "Atherosclerotic RAS — renal artery stenting",
        "Fibromuscular dysplasia — balloon angioplasty without stenting",
        "Takayasu arteritis — immunosuppression",
        "Polyarteritis nodosa — immunosuppression",
      ],
      correct: 1,
      explanation:
        "Fibromuscular dysplasia (FMD) is a non-inflammatory, non-atheromatous arteriopathy affecting young women. The renal arteries (distal 2/3) and carotid arteries are most commonly affected. The 'string of beads' appearance on angiography is pathognomonic — alternating stenoses and aneurysms from fibrous overgrowth. Balloon angioplasty (percutaneous transluminal angioplasty without stenting) is the treatment of choice and is often curative. Stenting is reserved for refractory cases.",
    },
    {
      type: "text",
      title: "Thrombotic Microangiopathies: HUS & TTP",
      content: `
        <p><span class="glossary-term">Thrombotic microangiopathy (TMA)</span> describes a group of conditions characterised by microvascular thrombosis causing <strong>microangiopathic haemolytic anaemia (MAHA)</strong>, thrombocytopaenia, and end-organ damage.</p>

        <div class="highlight-box">
          <p><strong>Haemolytic Uraemic Syndrome (HUS)</strong></p>
          <ul>
            <li><strong>Typical (Stx-HUS)</strong>: Shiga-toxin producing E. coli <span class="purple-term">O157:H7</span> → endothelial damage → microthrombi in renal vasculature
              <ul>
                <li>Prodrome: bloody diarrhoea, abdominal cramps (3–5 days) → AKI + MAHA + thrombocytopaenia</li>
                <li>Mainly affects children; self-limiting but may need dialysis</li>
                <li><strong>No antibiotics</strong> (lyse bacteria → ↑Shiga toxin release → worse outcomes) and no anti-motility agents</li>
              </ul>
            </li>
            <li><strong>Atypical HUS (aHUS)</strong>: complement dysregulation (factor H, MCP, C3, C3b mutations)
              <ul>
                <li>No diarrhoea prodrome; relapsing; often in adults</li>
                <li>Treatment: <span class="purple-term">eculizumab</span> (anti-C5 monoclonal antibody — blocks terminal complement) — revolutionised outcomes</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Thrombotic Thrombocytopaenic Purpura (TTP)</strong></p>
          <ul>
            <li><strong>Mechanism</strong>: ADAMTS13 deficiency (auto-antibody in acquired TTP; inherited in Upshaw-Schulman) → unclipped ultra-large vWF multimers → spontaneous platelet thrombi in microvasculature</li>
            <li><strong>Classic pentad</strong> ("FAT RN"): <span class="purple-term">Fever, MAHA, Thrombocytopaenia, Renal impairment (mild), Neurological</span> (fluctuating confusion, TIAs, seizures)</li>
            <li><strong>ADAMTS13 activity &lt;10%</strong> — confirms TTP</li>
            <li>Do NOT transfuse platelets (fuel thrombus formation)</li>
            <li><strong>Treatment</strong>: plasmapheresis (removes auto-antibody + replaces ADAMTS13) + steroids; caplacizumab (anti-vWF) reduces MAHA and thrombosis; rituximab (for relapsing TTP)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">HUS vs TTP</div>
          <div class="mnemonic-content"><strong>HUS</strong> = Haematuria + Uraemia + Stool (bloody diarrhoea) = mainly RENAL + Shiga toxin<br/><strong>TTP</strong> = ThrombocytoPaenia + neuroLogical + ADAMTS13 = BRAIN + low ADAMTS13</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 6-year-old develops bloody diarrhoea followed by oliguria, haematuria, elevated creatinine, MAHA, and thrombocytopaenia. E. coli O157:H7 is isolated from stool. Why should antibiotics be withheld?",
      options: [
        "Antibiotics cause direct nephrotoxicity in children",
        "Antibiotics lyse the bacteria, releasing more Shiga toxin and worsening the haemolytic uraemic syndrome",
        "The infection is self-limiting and antibiotics have no benefit",
        "Antibiotics cause antibiotic-associated diarrhoea, worsening fluid losses",
      ],
      correct: 1,
      explanation:
        "In Shiga toxin-producing E. coli (STEC) infection, antibiotics lyse the bacterial cell walls → massive release of preformed Shiga toxin → systemic absorption → worsens endothelial damage → worse HUS outcomes. Multiple studies have confirmed this risk. Anti-motility agents (loperamide) are also contraindicated as they increase toxin contact time with the gut. Management is supportive: IV fluids, blood transfusion, and dialysis if needed.",
    },
    {
      type: "question",
      text: "A 30-year-old woman presents with confusion, fever, thrombocytopaenia, MAHA, and mild renal impairment. ADAMTS13 activity is 5%. What is the diagnosis and the first-line treatment?",
      options: [
        "Haemolytic uraemic syndrome — supportive care",
        "Thrombotic thrombocytopaenic purpura — plasmapheresis",
        "Disseminated intravascular coagulation — fresh frozen plasma",
        "Aplastic anaemia — immunosuppression",
      ],
      correct: 1,
      explanation:
        "ADAMTS13 activity <10% is diagnostic of TTP. The pentad (fever, MAHA, thrombocytopaenia, renal impairment, neurological symptoms) with ADAMTS13 deficiency confirms acquired TTP. First-line treatment is plasma exchange (plasmapheresis), which removes the anti-ADAMTS13 autoantibody and replaces ADAMTS13 enzyme. Platelet transfusion is contraindicated (fuels further microthrombus formation). Mortality without plasmapheresis exceeds 90%.",
    },
    {
      type: "text",
      title: "Autosomal Dominant Polycystic Kidney Disease (ADPKD)",
      content: `
        <p><span class="glossary-term">ADPKD</span> is the most common inherited kidney disease (~1 in 400–1000). Bilateral renal cysts progressively enlarge, destroying renal parenchyma and leading to ESKD.</p>

        <div class="highlight-box">
          <p><strong>Genetics</strong></p>
          <ul>
            <li><strong>PKD1</strong> (85%) — chromosome 16p, polycystin-1; ESKD ~55 years</li>
            <li><strong>PKD2</strong> (15%) — chromosome 4q, polycystin-2; ESKD ~75 years (milder course)</li>
            <li>Autosomal dominant — 50% chance of transmission; new mutations uncommon</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Extrarenal manifestations</strong></p>
          <ul>
            <li><strong>Hepatic cysts</strong> — most common extrarenal feature (~80%); usually asymptomatic</li>
            <li><strong>Intracranial (Berry) aneurysms</strong> — ~10%; risk of subarachnoid haemorrhage; screen with MRA in those with family history of SAH or before high-risk activities</li>
            <li><strong>Mitral valve prolapse</strong> (~25%)</li>
            <li>Colonic diverticula, pancreatic cysts, seminal vesicle cysts</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Renal features</strong></p>
          <ul>
            <li>Hypertension (early — RAAS activation from cyst compression of intrarenal arteries)</li>
            <li>Flank pain and haematuria (cyst haemorrhage or rupture)</li>
            <li>UTIs / cyst infections (difficult to treat — fluoroquinolones penetrate cysts best)</li>
            <li>Nephrolithiasis (~20%; uric acid and calcium oxalate stones)</li>
            <li>Progressive CKD → ESKD (dialysis or transplant)</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: <span class="purple-term">Tolvaptan</span> (V2 receptor antagonist → ↓cAMP → slows cyst epithelial proliferation) — approved for rapidly progressive ADPKD (CKD stage 1–3). BP control with ACEi/ARBs. Avoid caffeine (↑cAMP). Screening for intracranial aneurysms in high-risk families.</p>
      `,
    },
    {
      type: "question",
      text: "A 30-year-old with ADPKD suddenly develops the worst headache of his life, vomiting, and a stiff neck. What is the most likely complication and what investigation confirms it?",
      options: [
        "Cyst haemorrhage — renal ultrasound",
        "Subarachnoid haemorrhage from a ruptured intracranial aneurysm — CT head (non-contrast) then lumbar puncture if CT negative",
        "Hypertensive emergency — CT head to exclude haemorrhage",
        "Meningitis from a cyst infection — lumbar puncture for CSF analysis",
      ],
      correct: 1,
      explanation:
        "Intracranial (Berry) aneurysms occur in ~10% of ADPKD patients. Rupture causes subarachnoid haemorrhage (SAH) — the classic 'thunderclap headache' (worst headache of life), meningism (neck stiffness, photophobia), and vomiting. Investigation: CT head (non-contrast, first-line; shows blood in 95% if done within 6 hours) → if negative, lumbar puncture (xanthochromia). ADPKD patients with family history of SAH should be screened with MR angiography.",
    },
    {
      type: "question",
      text: "Which medication slows the progression of cyst growth in ADPKD and what is its mechanism?",
      options: [
        "ACE inhibitors — reduce intraglomerular pressure",
        "Tolvaptan — V2 receptor antagonist that reduces cAMP-driven cyst epithelial proliferation",
        "Furosemide — reduces fluid accumulation in cysts",
        "Desmopressin — reduces ADH-driven cyst growth",
      ],
      correct: 1,
      explanation:
        "Tolvaptan is a selective V2 receptor (vasopressin receptor) antagonist. ADH acting on V2 receptors in renal cyst epithelium increases cAMP → promotes cyst fluid secretion and epithelial proliferation. Tolvaptan blocks this → ↓cAMP → slower cyst growth and slower GFR decline. It is approved for adults with rapidly progressive ADPKD (CKD stage 1–3). Side effects: aquaretic (thirst, polyuria), hepatotoxicity (monitoring required).",
    },
  ],
};
